import type { PayloadAction } from '@reduxjs/toolkit'
// eslint-disable-next-line no-duplicate-imports
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../middleware/api/apiList'
import {
  CheckListSliceState,
  CurrentChecklistInfoSettingsPage,
  GetChecklistParams,
  IncomingChecklistItem,
  OutgoingAddPage,
} from '../../types/Checklist/ChecklistTypes'
import ChecklistApi from '../../middleware/api/Checklist/ChecklistApi'
import { ValidationError } from '../../types/commonTypes'
import { initialChecklistItem } from '../../pages/Checklist/ChecklistHelpers'

const initialCheckListSlice: CheckListSliceState = {
  isLoading: ApiLoadingState.idle,
  error: null,
  incomingChecklist: { size: 0, list: [] },
  checkListInfoSettingsPageToggle:
    CurrentChecklistInfoSettingsPage.checkListInfoSettings,
  empDepartments: [],
  checklistParams: { endIndex: 20, startIndex: 0 },
  editThisPage: initialChecklistItem, // this is to edit the page
  checklistInfoSettingsParams: { endIndex: 20, startIndex: 0 },
  clickedChecklistTitle: initialChecklistItem, // this is for the description
}

const getCheckListThunk = createAsyncThunk(
  'Checklist/getCheckListThunk',
  async (finalParams: GetChecklistParams, thunkApi) => {
    try {
      return await ChecklistApi.getChecklist(finalParams)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getChecklistItemThunk = createAsyncThunk(
  'Checklist/getChecklistItemThunk',
  async (pageName: string, thunkApi) => {
    try {
      return await ChecklistApi.getChecklistItem(pageName)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getEmpDepartmentsThunk = createAsyncThunk(
  'Checklist/getEmpDepartmentsThunk',
  async (_, thunkApi) => {
    try {
      return await ChecklistApi.getEmpDepartments()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const addPageThunk = createAsyncThunk(
  'Checklist/addPageThunk',
  async (finalData: OutgoingAddPage, thunkApi) => {
    try {
      return await ChecklistApi.addPage(finalData)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const updateHandbookItemThunk = createAsyncThunk(
  'Checklist/updateHandbookItemThunk',
  async (finalData: IncomingChecklistItem, thunkApi) => {
    try {
      return await ChecklistApi.updateHandbookItem(finalData)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const deletePageThunk = createAsyncThunk(
  'Checklist/deletePageThunk',
  async (bookId: number, thunkApi) => {
    try {
      return await ChecklistApi.deletePage(bookId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const checkListSlice = createSlice({
  name: 'Checklist',
  initialState: initialCheckListSlice,
  reducers: {
    setChecklistParams: (state, action: PayloadAction<GetChecklistParams>) => {
      state.checklistParams = action.payload
    },
    setChecklistInfoSettingsParams: (
      state,
      action: PayloadAction<GetChecklistParams>,
    ) => {
      state.checklistInfoSettingsParams = action.payload
    },
    setChecklistInfoSettingsToggle: (
      state,
      action: PayloadAction<CurrentChecklistInfoSettingsPage>,
    ) => {
      state.checkListInfoSettingsPageToggle = action.payload
    },
    setEditThisPage: (state, action: PayloadAction<IncomingChecklistItem>) => {
      state.editThisPage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCheckListThunk.fulfilled, (state, action) => {
      state.incomingChecklist = action.payload
    })
    builder.addCase(getChecklistItemThunk.fulfilled, (state, action) => {
      state.clickedChecklistTitle = action.payload
    })
    builder.addCase(getEmpDepartmentsThunk.fulfilled, (state, action) => {
      state.empDepartments = action.payload
    })
    builder.addMatcher(
      isAnyOf(
        getCheckListThunk.pending,
        getChecklistItemThunk.pending,
        getEmpDepartmentsThunk.pending,
        addPageThunk.pending,
        updateHandbookItemThunk.pending,
        deletePageThunk.pending,
      ),
      (state) => {
        state.isLoading = ApiLoadingState.loading
        state.error = null
      },
    )
    builder.addMatcher(
      isAnyOf(
        getCheckListThunk.fulfilled,
        getChecklistItemThunk.fulfilled,
        getEmpDepartmentsThunk.fulfilled,
        addPageThunk.fulfilled,
        updateHandbookItemThunk.fulfilled,
        deletePageThunk.fulfilled,
      ),
      (state) => {
        state.isLoading = ApiLoadingState.succeeded
      },
    )
    builder.addMatcher(
      isAnyOf(
        getCheckListThunk.rejected,
        getChecklistItemThunk.rejected,
        getEmpDepartmentsThunk.rejected,
        addPageThunk.rejected,
        updateHandbookItemThunk.rejected,
        deletePageThunk.rejected,
      ),
      (state, action) => {
        state.error = action.payload as ValidationError
        state.isLoading = ApiLoadingState.failed
      },
    )
  },
})

const ChecklistThunks = {
  getCheckListThunk,
  getChecklistItemThunk,
  getEmpDepartmentsThunk,
  addPageThunk,
  updateHandbookItemThunk,
  deletePageThunk,
}

export const ChecklistServices = {
  ...ChecklistThunks,
  actions: checkListSlice.actions,
}

const ChecklistReducer = checkListSlice.reducer
export default ChecklistReducer
